import clsx from "clsx";

import { useUserMutation, useWorkspaceRoles } from "@web-app/api";
import { Avatar, SandboxBadge, useAlert } from "@web-app/components";
import useDispatchStore from "@web-app/store/dispatchStore";
import type { Workspace } from "@web-app/types";

import { Icon } from "../Icon";
import SearchableDropdown from "../SearchableDropdown";

export default function WorkspaceSwitcher({
  currentWorkspace,
  isExpanded,
}: {
  currentWorkspace?: Workspace;
  isExpanded: boolean;
}) {
  const { showAlertAsync } = useAlert();
  const user = useDispatchStore((state) => state.user);
  const { switchWorkspace } = useUserMutation();

  const { data: workspaceRoles } = useWorkspaceRoles();
  const handleUpdateWorkspace = async (option: { value: string }) => {
    const workspaceId = option.value;
    await showAlertAsync(switchWorkspace.mutateAsync(workspaceId), {
      error: {
        message: "Failed to switch workspace",
        title: "Error",
        type: "error",
      },
      loading: {
        message: "Switching workspace...",
        title: "Switching",
        type: "default",
      },
      success: {
        message: "Workspace switched successfully. Refreshing ...",
        title: "Success",
        type: "success",
      },
    }).then(() => {
      window.location.reload();
    });
  };

  return (
    <div className="lg:mt-5 flex shrink-0 items-center lg:px-5">
      {user && currentWorkspace && (
        <div className="flex cursor-pointer items-center gap-2 relative">
          <SearchableDropdown
            panelClassName="right-[-280px] top-[0px] transition duration-200 ease-out"
            options={(workspaceRoles ?? [])
              .map((role) => ({
                avatar: {
                  name: role.workspace.name,
                  url: role.workspace?.avatarUrl || "",
                },
                id: role.id,
                isSandbox: role.workspace.type === "sandbox",
                label: role.workspace.name,
                value: role.workspace.id,
              }))
              .filter((role) => role.value !== currentWorkspace.id)
              .sort((a, b) => a.label.localeCompare(b.label))}
            onSelect={handleUpdateWorkspace}
            buttonComponent={
              <div className="flex gap-3 items-center">
                <div className="relative shrink-0">
                  <Avatar
                    name={currentWorkspace.name || ""}
                    size={10}
                    image={currentWorkspace.avatarUrl || ""}
                  />
                  {currentWorkspace.type === "sandbox" && (
                    <SandboxBadge className="absolute -bottom-0.5 -right-0.5 h-4 w-4" />
                  )}
                </div>
                <span
                  className={clsx(
                    "text-sm font-semibold text-gray-700 flex items-center gap-4",
                    isExpanded ? "flex" : "hidden",
                  )}
                >
                  <span className="text-ellipsis w-[125px] overflow-hidden whitespace-nowrap">
                    {currentWorkspace.name}
                  </span>

                  {workspaceRoles && workspaceRoles.length > 1 && (
                    <Icon name="chevronRight" />
                  )}
                </span>
              </div>
            }
            placeholder="Search Workspace"
          />
        </div>
      )}
    </div>
  );
}
